// React, Next
import { ReactNode, useContext } from 'react'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import breakpoints from '@/assets/theme/breakpoints'
import { getFontBody, getFontBodySmall } from '@/assets/theme/fonts'
import classes from './StatisticCard-CSS'

export type StatisticCardProps = {
  /** The headline figure, e.g. "88%" or "$1.3M". */
  value: string
  /** Small connecting word rendered between two figures, e.g. "vs" or "in". */
  note?: string
  /** Second figure shown after the note, e.g. the "42" of "5 vs 42". */
  suffix?: string
  text: ReactNode
  /** Attribution line. Omit it and the footer is not rendered at all. */
  source?: string
  /** Draws the accent border, for the one card that should stand out. */
  feature?: boolean
}

export default function StatisticCard(props: StatisticCardProps) {
  const { screenDimensions } = useContext(GlobalContext)

  const getFontNumber = () => {
    const w = screenDimensions.width
    if (w > breakpoints.desktopSmall) return '3.25rem'
    if (w > breakpoints.mobileSmall) return '2.75rem'
    return '2.25rem'
  }

  const getFontNote = () => {
    const w = screenDimensions.width
    if (w > breakpoints.desktopSmall) return '1.875rem'
    return '1.5rem'
  }

  // *** MAIN RENDER ***
  return (
    <Box
      component="article"
      sx={{
        ...classes.card,
        ...(props.feature ? classes.cardFeature : {})
      }}
    >
      <Typography
        component="p"
        sx={{ ...classes.number, fontSize: getFontNumber() }}
      >
        {props.value}
        {props.note && (
          <Box
            component="span"
            sx={{ ...classes.numberNote, fontSize: getFontNote() }}
          >
            {` ${props.note}`}
          </Box>
        )}
        {props.suffix && ` ${props.suffix}`}
      </Typography>

      <Typography
        component="p"
        sx={{ ...classes.body, fontSize: getFontBody() }}
      >
        {props.text}
      </Typography>

      {props.source && (
        <Typography
          component="p"
          sx={{ ...classes.source, fontSize: getFontBodySmall() }}
        >
          {props.source}
        </Typography>
      )}
    </Box>
  )
}
